import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { createDispatchers } from "@utils/dispatcher";
import { interpolateColor } from "@utils/chart-utils";
import { RootState } from "..";
import { QuestionResponsesSearchFilter } from "@/models/Filter";
import type { RollingAverageConfig } from "@/utils/rolling-average-utils";
import {
  type PointInTimeViewerOptions,
  DEFAULT_POINT_IN_TIME_VIEWER_OPTIONS,
} from "@models/Dataset";

// Focus mode for Single questions with multiple variables (similar to line chart)
export type BarFocusMode = "variable" | "value";

export interface BarFocusSelection {
  mode: BarFocusMode;
  selectedVariableId?: string;
  selectedValueId?: string;
}

// Time Period Selection (similar to crosstab)
export type TimePeriod =
  | "all_time"
  | "latest_quarter"
  | "previous_quarter"
  | "latest_semester"
  | "previous_semester"
  | "custom_period";

export interface TimePeriodSelection {
  timePeriod: TimePeriod;
  startDate: string | null; // mm/dd/yyyy format
  endDate: string | null; // mm/dd/yyyy format
}

// Statistical Testing (similar to crosstab)
export type StatTestType = "z-test" | "t-test";
export type SignificanceLevel = "90%" | "95%" | "99%";

export interface StatisticalTestingConfig {
  enabled: boolean;
  testType: StatTestType;
  significanceLevel: SignificanceLevel;
  minimumSampleSize: number;
}

export interface BarChartConfig {
  // Chart basic settings
  title: string;
  subtitle: string;
  titleBold: boolean;
  titleUnderline: boolean;

  // Question metadata
  questionLabel?: string;
  questionName?: string;
  questionType?: "Single" | "Multiple" | "Float" | "Number";

  // Focus mode (for Single questions with multiple variables)
  focusSelection: BarFocusSelection | null;

  // Focus parameters for API request (stores the actual label/value strings)
  focusMode?: string; // "VARIABLE" or "VALUE" (uppercase for API)
  focusEntity?: string; // variable.label or value.value (for API)
  focusEntityDisplayLabel?: string; // Display label as shown in dropdown (rowtitle/title for variables, title/label for values)

  // Orientation
  orientation: "horizontal" | "vertical";

  // Data formatting
  percentageFormatting: number;
  valuePlacement: "end" | "inside" | "none";
  weight: "DISABLED" | "DATASET_DEFAULT" | "SURVEY_WEIGHT";

  // Data sorting
  dataSorting: "ascending" | "descending" | "none";
  topNResponses: boolean;
  topNValue: number;
  combineRemainingIntoOther: boolean;

  // Styling & Customization
  colorPalette: string;
  labelsPlacement: "next" | "none";
  showLegend: boolean;
  truncateCategoryText: boolean;
  showValueAxis: boolean;
  compactMode: boolean;

  // Time Period Selection (PRD Module 1)
  timePeriodSelection: TimePeriodSelection;

  // Rolling Average (PRD-26)
  rollingAverage: RollingAverageConfig;
  effectiveRollingPeriodCount?: number;
  rollingPeriodsWarningMessage?: string;
  comparisonRollingLabel?: string;
  missingRollingPeriodLabels?: string[];

  // Date range constraints from API
  responsesMinDate?: string; // ISO date string (YYYY-MM-DD)
  responsesMaxDate?: string; // ISO date string (YYYY-MM-DD)

  // Statistical Testing (PRD Module 2)
  statisticalTesting: StatisticalTestingConfig;

  // Viewer Time Options 
  viewerOptions: PointInTimeViewerOptions;

  // Chart data
  categories: string[];
  series: Array<{
    name: string;
    data: number[];
    color?: string;
    colors?: string[]; // For multicolor support - individual colors per data point
  }>;

  // Original data (preserved for reset functionality)
  originalCategories: string[];
  originalSeries: Array<{
    name: string;
    data: number[];
    color?: string;
    colors?: string[]; // For multicolor support - individual colors per data point
  }>;
}
interface Questions extends Record<string, any> {
  id: string;
  label: string;
  name: string;
  responses: number;
}
export interface ChartConfigState {
  currentConfig: BarChartConfig;
  savedConfigs: BarChartConfig[];
  isDrawerOpen: boolean;
  previewMode: boolean;
  question: Questions | null;
  // Store the widget ID that's being edited instead of a callback
  editingWidgetId: string | null;
  // Store dataset and question IDs for refetching data
  datasetId: string | null;
  datasetName: string | null;
  questionId: string | null;
  datasetVersionId: number | null;
  // Store active filters for refetching data with filters
  activeFilters: QuestionResponsesSearchFilter[];
  // Toggle to apply/ignore filters in preview
  applyFilters: boolean;
}

const defaultBarChartConfig: BarChartConfig = {
  title: "Brand Tracking",
  subtitle: "",
  titleBold: false,
  titleUnderline: false,
  focusSelection: null,
  orientation: "vertical",
  percentageFormatting: 0,
  valuePlacement: "end",
  weight: "DISABLED",
  dataSorting: "none",
  topNResponses: false,
  topNValue: 10,
  combineRemainingIntoOther: false,
  colorPalette: "Blue",
  labelsPlacement: "next",
  showLegend: false,
  truncateCategoryText: false,
  showValueAxis: true,
  compactMode: false,
  timePeriodSelection: {
    timePeriod: "all_time",
    startDate: null,
    endDate: null,
  },
  rollingAverage: {
    enabled: false,
    windowSize: 2,
  },
  statisticalTesting: {
    enabled: false,
    testType: "t-test",
    significanceLevel: "90%",
    minimumSampleSize: 30,
  },
  viewerOptions: DEFAULT_POINT_IN_TIME_VIEWER_OPTIONS,
  categories: [],
  series: [],
  originalCategories: [],
  originalSeries: [],
};

export const initialState: ChartConfigState = {
  currentConfig: { ...defaultBarChartConfig },
  savedConfigs: [],
  isDrawerOpen: false,
  previewMode: true,
  question: null,
  editingWidgetId: null,
  datasetId: null,
  datasetName: null,
  questionId: null,
  datasetVersionId: null,
  activeFilters: [],
  applyFilters: true,
};

export const chartConfigSlice = createSlice({
  name: "chartConfig",
  initialState,
  reducers: {
    openChartDrawer: (state, action: PayloadAction<string>) => {
      state.isDrawerOpen = true;
      state.editingWidgetId = action.payload;
    },
    closeChartDrawer: (state) => {
      state.isDrawerOpen = false;
      state.editingWidgetId = null;
      // Clear focus mode parameters when closing drawer to prevent persistence across chart creations
      state.currentConfig.focusMode = undefined;
      state.currentConfig.focusEntity = undefined;
      state.currentConfig.focusEntityDisplayLabel = undefined;
      state.currentConfig.focusSelection = null;
    },
    togglePreviewMode: (state) => {
      state.previewMode = !state.previewMode;
    },
    updateChartTitle: (state, action: PayloadAction<string>) => {
      state.currentConfig.title = action.payload;
    },
    updateChartSubtitle: (state, action: PayloadAction<string>) => {
      state.currentConfig.subtitle = action.payload;
    },
    updateTitleBold: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.titleBold = action.payload;
    },
    updateTitleUnderline: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.titleUnderline = action.payload;
    },
    updateOrientation: (
      state,
      action: PayloadAction<"horizontal" | "vertical">,
    ) => {
      state.currentConfig.orientation = action.payload;
    },
    updatePercentageFormatting: (state, action: PayloadAction<number>) => {
      state.currentConfig.percentageFormatting = action.payload;
    },
    updateValuePlacement: (
      state,
      action: PayloadAction<"end" | "inside" | "none">,
    ) => {
      state.currentConfig.valuePlacement = action.payload;
    },
    updateWeight: (
      state,
      action: PayloadAction<"DISABLED" | "DATASET_DEFAULT" | "SURVEY_WEIGHT">,
    ) => {
      state.currentConfig.weight = action.payload;
    },
    updateDataSorting: (
      state,
      action: PayloadAction<"ascending" | "descending" | "none">,
    ) => {
      state.currentConfig.dataSorting = action.payload;
      // Note: When switching to "none" (Original survey order), we DON'T restore from originalSeries
      // because that would reset the colors to the original database colors.
      // Instead, we just change the sorting flag and let the chart component handle
      // displaying data in original order while keeping the current color palette.
      // The actual sorting/ordering is handled in the chart component.
    },
    updateTopNResponses: (
      state,
      action: PayloadAction<{ enabled: boolean; value?: number }>,
    ) => {
      state.currentConfig.topNResponses = action.payload.enabled;
      if (action.payload.value !== undefined) {
        state.currentConfig.topNValue = action.payload.value;
      }
    },
    updateCombineRemainingIntoOther: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.currentConfig.combineRemainingIntoOther = action.payload;
    },
    updateColorPalette: (state, action: PayloadAction<string>) => {
      state.currentConfig.colorPalette = action.payload;

      // Update series colors based on palette
      const colorMap: Record<string, string> = {
        Orange: "#ffb09c",
        Green: "#57dfbe",
        Black: "#757679",
        Purple: "#a1a7df",
        Crimson: "#c36179",
        "Dark Green": "#57a68c",
        Blue: "#57bfe7",
        Yellow: "#ebdd7c",
      };

      if (action.payload === "Multicolor") {
        // For multicolor, assign different colors to each data point or series
        const multiColors = [
          "#ffb09c",
          "#57dfbe",
          "#757679",
          "#a1a7df",
          "#c36179",
          "#57a68c",
          "#57bfe7",
          "#ebdd7c",
        ];

        // If we have multiple series with single data points (typical bar chart structure)
        // color each series differently
        if (
          state.currentConfig.series.length > 1 &&
          state.currentConfig.series.every((s) => s.data.length === 1)
        ) {
          state.currentConfig.series.forEach((series, seriesIndex) => {
            const colorIndex = seriesIndex % multiColors.length;
            series.color = multiColors[colorIndex];
            delete series.colors; // Use series-level color instead
          });
        } else {
          // Otherwise, color individual data points within series
          state.currentConfig.series.forEach((series) => {
            series.data.forEach((_, dataIndex) => {
              const colorIndex = dataIndex % multiColors.length;
              if (!series.colors) series.colors = [];
              series.colors[dataIndex] = multiColors[colorIndex];
            });
          });
        }
      } else if (action.payload === "Gradient") {
        // For gradient, interpolate between #019EDB and #00CF9C
        const startColor = "#019EDB";
        const endColor = "#00CF9C";

        // If we have multiple series with single data points (typical bar chart structure)
        // apply gradient across series
        if (
          state.currentConfig.series.length > 1 &&
          state.currentConfig.series.every((s) => s.data.length === 1)
        ) {
          const seriesCount = state.currentConfig.series.length;
          state.currentConfig.series.forEach((series, seriesIndex) => {
            const factor =
              seriesCount > 1 ? seriesIndex / (seriesCount - 1) : 0;
            const interpolatedColor = interpolateColor(
              startColor,
              endColor,
              factor,
            );
            series.color = interpolatedColor;
            delete series.colors; // Use series-level color instead
          });
        } else {
          // Otherwise, apply gradient to data points within each series
          state.currentConfig.series.forEach((series) => {
            const dataLength = series.data.length;
            series.data.forEach((_, dataIndex) => {
              const factor = dataLength > 1 ? dataIndex / (dataLength - 1) : 0;
              const interpolatedColor = interpolateColor(
                startColor,
                endColor,
                factor,
              );

              if (!series.colors) series.colors = [];
              series.colors[dataIndex] = interpolatedColor;
            });
          });
        }
      } else if (action.payload === "Grayscale") {
        // For grayscale gradient, interpolate between light gray and dark gray
        const startColor = "#f0f0f0"; // Light gray
        const endColor = "#404040"; // Dark gray

        // If we have multiple series with single data points (typical bar chart structure)
        // apply gradient across series
        if (
          state.currentConfig.series.length > 1 &&
          state.currentConfig.series.every((s) => s.data.length === 1)
        ) {
          const seriesCount = state.currentConfig.series.length;
          state.currentConfig.series.forEach((series, seriesIndex) => {
            const factor =
              seriesCount > 1 ? seriesIndex / (seriesCount - 1) : 0;
            const interpolatedColor = interpolateColor(
              startColor,
              endColor,
              factor,
            );
            series.color = interpolatedColor;
            delete series.colors; // Use series-level color instead
          });
        } else {
          // Otherwise, apply gradient to data points within each series
          state.currentConfig.series.forEach((series) => {
            const dataLength = series.data.length;
            series.data.forEach((_, dataIndex) => {
              const factor = dataLength > 1 ? dataIndex / (dataLength - 1) : 0;
              const interpolatedColor = interpolateColor(
                startColor,
                endColor,
                factor,
              );

              if (!series.colors) series.colors = [];
              series.colors[dataIndex] = interpolatedColor;
            });
          });
        }
      } else {
        // For single color palettes, use one color for the entire series
        const newColor = colorMap[action.payload] || "#57bfe7";
        state.currentConfig.series.forEach((series) => {
          series.color = newColor;
          // Clear individual colors when using single color
          delete series.colors;
        });
      }
    },
    updateLabelsPlacement: (state, action: PayloadAction<"next" | "none">) => {
      state.currentConfig.labelsPlacement = action.payload;
    },
    updateShowLegend: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.showLegend = action.payload;
    },
    updateQuestions: (state, action: PayloadAction<Questions | null>) => {
      state.question = action.payload;
      // Only set default chart title if it's the default "Brand Tracking" title
      // This allows new charts to get question name, but preserves custom titles when editing
      if (
        action.payload?.name &&
        state.currentConfig.title === "Brand Tracking"
      ) {
        state.currentConfig.title = action.payload.name;
      }
      // Store question metadata in config for persistence
      if (action.payload) {
        state.currentConfig.questionLabel = action.payload.label;
        state.currentConfig.questionName = action.payload.name;
        state.currentConfig.questionType = action.payload.type as
          | "Single"
          | "Multiple"
          | "Float"
          | "Number"
          | undefined;
      }
    },
    updateFocusSelection: (
      state,
      action: PayloadAction<BarFocusSelection | null>,
    ) => {
      state.currentConfig.focusSelection = action.payload;
    },
    updateFocusParams: (
      state,
      action: PayloadAction<{
        focusMode: string;
        focusEntity: string;
        focusEntityDisplayLabel?: string;
      } | null>,
    ) => {
      if (action.payload) {
        state.currentConfig.focusMode = action.payload.focusMode;
        state.currentConfig.focusEntity = action.payload.focusEntity;
        state.currentConfig.focusEntityDisplayLabel =
          action.payload.focusEntityDisplayLabel;
      } else {
        state.currentConfig.focusMode = undefined;
        state.currentConfig.focusEntity = undefined;
        state.currentConfig.focusEntityDisplayLabel = undefined;
      }
    },
    updateChartMetadata: (
      state,
      action: PayloadAction<{
        datasetId: string;
        datasetName?: string;
        questionId: string;
        datasetVersionId?: number | null;
        activeFilters?: QuestionResponsesSearchFilter[];
      }>,
    ) => {
      state.datasetId = action.payload.datasetId;
      state.datasetName = action.payload.datasetName ?? null;
      state.questionId = action.payload.questionId;
      state.datasetVersionId = action.payload.datasetVersionId ?? null;
      state.activeFilters = action.payload.activeFilters ?? [];
    },
    updateTruncateCategoryText: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.truncateCategoryText = action.payload;
    },
    updateShowValueAxis: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.showValueAxis = action.payload;
    },
    updateCompactMode: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.compactMode = action.payload;
    },
    updateChartData: (
      state,
      action: PayloadAction<{
        categories: string[];
        series: BarChartConfig["series"];
        responsesMinDate?: string;
        responsesMaxDate?: string;
        effectiveRollingPeriodCount?: number;
        rollingPeriodsWarningMessage?: string;
        comparisonRollingLabel?: string;
        missingRollingPeriodLabels?: string[];
      }>,
    ) => {
      const { responsesMinDate, responsesMaxDate, effectiveRollingPeriodCount, rollingPeriodsWarningMessage, comparisonRollingLabel, missingRollingPeriodLabels, ...data } = action.payload;
      // Create deep copies to avoid proxy issues
      const categories = [...data.categories];
      const series = data.series.map((s) => ({
        ...s,
        data: [...s.data],
        colors: s.colors ? [...s.colors] : undefined,
      }));

      state.currentConfig.categories = categories;
      state.currentConfig.series = series;
      // Store deep copies as original data to avoid proxy revocation issues
      state.currentConfig.originalCategories = [...categories];
      state.currentConfig.originalSeries = series.map((s) => ({
        ...s,
        data: [...s.data],
        colors: s.colors ? [...s.colors] : undefined,
      }));

      // Update date range constraints if provided
      if (responsesMinDate) {
        state.currentConfig.responsesMinDate = responsesMinDate;
      }
      if (responsesMaxDate) {
        state.currentConfig.responsesMaxDate = responsesMaxDate;
      }

      // Update rolling average response fields
      state.currentConfig.effectiveRollingPeriodCount = effectiveRollingPeriodCount;
      state.currentConfig.rollingPeriodsWarningMessage = rollingPeriodsWarningMessage;
      state.currentConfig.comparisonRollingLabel = comparisonRollingLabel;
      state.currentConfig.missingRollingPeriodLabels = missingRollingPeriodLabels;
    },
    clearRollingAverageResponseFields: (state) => {
      state.currentConfig.effectiveRollingPeriodCount = undefined;
      state.currentConfig.rollingPeriodsWarningMessage = undefined;
      state.currentConfig.comparisonRollingLabel = undefined;
      state.currentConfig.missingRollingPeriodLabels = undefined;
    },
    saveCurrentConfig: (state) => {
      const configToSave = { ...state.currentConfig };
      const tp = configToSave.timePeriodSelection?.timePeriod;
      if (tp && tp !== "all_time" && tp !== "custom_period") {
        configToSave.timePeriodSelection = {
          ...configToSave.timePeriodSelection,
          startDate: null,
          endDate: null,
        };
      }
      state.savedConfigs.push(configToSave);
    },
    loadConfig: (state, action: PayloadAction<BarChartConfig>) => {
      // When loading a saved config, use the original data from the saved config
      // Only preserve current original data if it exists and the loaded config doesn't have it
      const hasCurrentOriginalData =
        state.currentConfig.originalCategories.length > 0;
      const hasLoadedOriginalData =
        action.payload.originalCategories &&
        action.payload.originalCategories.length > 0;

      // Merge defaults for fields that may be absent in older saved configs
      const payload: BarChartConfig = {
        ...action.payload,
        rollingAverage: action.payload.rollingAverage ?? { enabled: false, windowSize: 2 },
        statisticalTesting: action.payload.statisticalTesting ?? { enabled: false },
        timePeriodSelection: action.payload.timePeriodSelection ?? { timePeriod: "all_time", startDate: null, endDate: null },
      };

      Object.assign(state.currentConfig, payload);

      // If the loaded config doesn't have original data but we have current original data, preserve it
      if (!hasLoadedOriginalData && hasCurrentOriginalData) {
        state.currentConfig.originalCategories =
          state.currentConfig.originalCategories;
        state.currentConfig.originalSeries = state.currentConfig.originalSeries;
      }
      // Otherwise, use the original data from the loaded config (which should be the correct behavior)
    },
    toggleApplyFilters: (state) => {
      state.applyFilters = !state.applyFilters;
    },
    setApplyFilters: (state, action: PayloadAction<boolean>) => {
      state.applyFilters = action.payload;
    },
    // Time Period Selection actions
    updateTimePeriod: (state, action: PayloadAction<TimePeriod>) => {
      state.currentConfig.timePeriodSelection.timePeriod = action.payload;
      // Reset dates when changing time period
      if (action.payload !== "custom_period") {
        state.currentConfig.timePeriodSelection.startDate = null;
        state.currentConfig.timePeriodSelection.endDate = null;
      }
      // Clear stale rolling average response data so footer reflects new period immediately
      state.currentConfig.effectiveRollingPeriodCount = undefined;
      state.currentConfig.rollingPeriodsWarningMessage = undefined;
      state.currentConfig.comparisonRollingLabel = undefined;
      state.currentConfig.missingRollingPeriodLabels = undefined;
    },
    updateTimePeriodDates: (
      state,
      action: PayloadAction<{
        startDate: string | null;
        endDate: string | null;
      }>,
    ) => {
      state.currentConfig.timePeriodSelection.startDate =
        action.payload.startDate;
      state.currentConfig.timePeriodSelection.endDate = action.payload.endDate;
    },

    // Rolling Average actions
    updateRollingAverageEnabled: (state, action: PayloadAction<boolean>) => {
      if (!state.currentConfig.rollingAverage) {
        state.currentConfig.rollingAverage = { enabled: false, windowSize: 2 };
      }
      state.currentConfig.rollingAverage.enabled = action.payload;
      if (!action.payload) {
        state.currentConfig.rollingAverage.windowSize = 2;
      }
    },
    updateRollingAverageWindowSize: (state, action: PayloadAction<number>) => {
      if (!state.currentConfig.rollingAverage) {
        state.currentConfig.rollingAverage = { enabled: false, windowSize: 2 };
      }
      state.currentConfig.rollingAverage.windowSize = action.payload;
      // Clear stale API response data so footer reflects new windowSize immediately
      state.currentConfig.effectiveRollingPeriodCount = undefined;
      state.currentConfig.rollingPeriodsWarningMessage = undefined;
      state.currentConfig.comparisonRollingLabel = undefined;
      state.currentConfig.missingRollingPeriodLabels = undefined;
    },

    // Statistical Testing actions
    updateStatTestingEnabled: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.statisticalTesting.enabled = action.payload;
    },
    updateStatTestType: (state, action: PayloadAction<StatTestType>) => {
      state.currentConfig.statisticalTesting.testType = action.payload;
    },
    updateSignificanceLevel: (
      state,
      action: PayloadAction<SignificanceLevel>,
    ) => {
      state.currentConfig.statisticalTesting.significanceLevel = action.payload;
    },
    updateMinimumSampleSize: (state, action: PayloadAction<number>) => {
      state.currentConfig.statisticalTesting.minimumSampleSize = action.payload;
    },

    updateViewerOptions: (state, action: PayloadAction<PointInTimeViewerOptions>) => {
      state.currentConfig.viewerOptions = action.payload;
    },

    resetToDefault: (state) => {
      // Preserve the original database data when resetting to default (if any exists)
      const originalCategories =
        state.currentConfig.originalCategories.length > 0
          ? [...state.currentConfig.originalCategories]
          : [];
      const originalSeries =
        state.currentConfig.originalSeries.length > 0
          ? state.currentConfig.originalSeries.map((series) => ({
              ...series,
              data: [...series.data],
            }))
          : [];

      Object.assign(state.currentConfig, {
        ...defaultBarChartConfig,
        // Keep the original database data if it exists, otherwise keep empty arrays
        originalCategories,
        originalSeries,
        // Also restore the current data to original order (or empty if no data)
        categories: originalCategories,
        series: originalSeries,
        // Clear focus mode parameters to prevent them from persisting
        focusMode: undefined,
        focusEntity: undefined,
        focusEntityDisplayLabel: undefined,
      });
    },
  },
});

export const {
  openChartDrawer,
  closeChartDrawer,
  togglePreviewMode,
  updateChartTitle,
  updateChartSubtitle,
  updateTitleBold,
  updateTitleUnderline,
  updateOrientation,
  updatePercentageFormatting,
  updateValuePlacement,
  updateWeight,
  updateDataSorting,
  updateTopNResponses,
  updateCombineRemainingIntoOther,
  updateColorPalette,
  updateLabelsPlacement,
  updateShowLegend,
  updateTruncateCategoryText,
  updateShowValueAxis,
  updateCompactMode,
  updateChartData,
  updateQuestions,
  updateFocusSelection,
  updateFocusParams,
  updateChartMetadata,
  updateTimePeriod,
  updateTimePeriodDates,
  updateRollingAverageEnabled,
  updateRollingAverageWindowSize,
  updateStatTestingEnabled,
  updateStatTestType,
  updateSignificanceLevel,
  updateMinimumSampleSize,
  updateViewerOptions,
  toggleApplyFilters,
  setApplyFilters,
  saveCurrentConfig,
  loadConfig,
  resetToDefault,
} = chartConfigSlice.actions;

export const chartConfigDispatcher = createDispatchers(
  chartConfigSlice.actions,
);
export const chartConfigSelector = (state: RootState) => state.chartConfig;

export const chartConfigReducer = chartConfigSlice.reducer;
